import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Gem } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import FileDrop from "@/components/site/FileDrop";

const inputCls =
  "w-full bg-[#1a1917] border border-[#3a372f] px-4 py-3.5 text-sm text-[#F3EDE3] placeholder-[#5C564C] focus:outline-none focus:border-[#D4AF37] transition-colors";

export default function ValuationForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", details: "", era: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await api.post("/valuations", fd);
      toast.success("Valuation request received — our experts reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", details: "", era: "" });
      setFiles([]);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your valuation request"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="valuation" className="bg-[#121110] text-[#F3EDE3] py-24 md:py-32" data-testid="valuation-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] gold-text flex items-center gap-3">
            <span className="inline-block w-10 h-px bg-[#B38728]" /> 03 — Free Valuation Service
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-5">
            What's your collection <span className="italic gold-text">really</span> worth?
          </h2>
          <p className="text-[#a79f90] text-sm md:text-base leading-relaxed mt-6 max-w-md">
            Send us photos or a quick video of your LEGO — loose minifigures, sealed sets, whole childhood bins.
            Our experts appraise thousands of pieces and reply with an honest market valuation within 24 hours. Zero cost, zero obligation.
          </p>
          <ul className="mt-8 space-y-4">
            {["Multi-point authenticity check on every piece", "Market pricing from real recent sales", "No obligation to sell — the appraisal is yours"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-[#a79f90]">
                <Gem className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="bg-[#1a1917]/60 border border-[#3a372f] p-8 space-y-5"
          data-testid="valuation-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#a79f90]">Full Name *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="valuation-name-input" className={`mt-2 ${inputCls}`} placeholder="Alex Brickson" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#a79f90]">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="valuation-email-input" className={`mt-2 ${inputCls}`} placeholder="you@email.com" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#a79f90]">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="valuation-phone-input" className={`mt-2 ${inputCls}`} placeholder="Optional" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#a79f90]">Estimated Year / Era</label>
              <input value={form.era} onChange={(e) => setForm({ ...form, era: e.target.value })} data-testid="valuation-era-input" className={`mt-2 ${inputCls}`} placeholder="e.g. 1999–2005 Star Wars" />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#a79f90]">What do you have? *</label>
            <textarea required rows={4} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="valuation-details-input" className={`mt-2 ${inputCls}`} placeholder="Sets, minifigures, condition, box status…" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#a79f90] block mb-2">Photos / Videos</label>
            <FileDrop files={files} setFiles={setFiles} testId="valuation-file-drop" dark />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="valuation-submit-button"
            className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-[#F3EDE3] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            {sending ? "Sending…" : "Get My Free Valuation"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
